import React, { useEffect } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { Ruler } from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  CounterInput,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useMenstrualHealth } from "../MenstrualHealthContext";
import { buildMenstrualHealthNavigationStack } from "../buildMenstrualHealthNavigationStack";
import type { MenstrualHealthStackParamList } from "../MenstrualHealthNavigator";
import { FLOW_INTENSITY_OPTIONS } from "../options";
import type { FlowIntensity } from "../options";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

export function CycleDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useMenstrualHealth();
  const navigation =
    useNavigation<NavigationProp<MenstrualHealthStackParamList>>();
  const theme = useMedicalHistoryTheme();

  const minLength = state.minAverageCycleLength ?? 25;
  const maxLength = state.maxAverageCycleLength ?? 30;

  // Commit defaults into state so displayed values match state
  useEffect(() => {
    if (
      state.minAverageCycleLength === null ||
      state.maxAverageCycleLength === null
    ) {
      updateState({
        minAverageCycleLength: state.minAverageCycleLength ?? 25,
        maxAverageCycleLength: state.maxAverageCycleLength ?? 30,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFlowSelect = (intensity: FlowIntensity) => {
    updateState({ menstrualFlowIntensity: intensity });
  };

  const handleSave = async () => {
    await save();
    const stack = buildMenstrualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("CycleDetails");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const isValid =
    state.minAverageCycleLength !== null &&
    state.maxAverageCycleLength !== null &&
    state.menstrualFlowIntensity !== null;

  return (
    <QuestionLayout
      title={t("med_history.gynae.menstrual_health.cycle_details_title")}
      subtitle={t("med_history.gynae.menstrual_health.cycle_details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.gynae.menstrual_health.continue")}
    >
      <View style={styles.container}>
        <CounterInput
          label={t("med_history.gynae.menstrual_health.min_cycle_length")}
          icon={Ruler}
          unit={t("med_history.gynae.menstrual_health.days")}
          value={minLength}
          min={15}
          max={maxLength}
          onIncrement={() =>
            updateState({
              minAverageCycleLength: Math.min(minLength + 1, maxLength),
            })
          }
          onDecrement={() =>
            updateState({
              minAverageCycleLength: Math.max(minLength - 1, 15),
            })
          }
        />

        <View style={styles.spacer} />

        <CounterInput
          label={t("med_history.gynae.menstrual_health.max_cycle_length")}
          icon={Ruler}
          unit={t("med_history.gynae.menstrual_health.days")}
          value={maxLength}
          min={minLength}
          max={60}
          onIncrement={() =>
            updateState({
              maxAverageCycleLength: Math.min(maxLength + 1, 60),
            })
          }
          onDecrement={() =>
            updateState({
              maxAverageCycleLength: Math.max(maxLength - 1, minLength),
            })
          }
        />

        <View style={styles.spacer} />

        <Text
          style={{ color: theme.cardLabel }}
          className="mb-3 pl-1 text-sm font-medium tracking-wide"
        >
          {t("med_history.gynae.menstrual_health.flow_intensity_label")}
        </Text>
        <View style={styles.flowRow}>
          {FLOW_INTENSITY_OPTIONS.map((option) => (
            <SquareOptionButton
              key={option.id}
              icon={option.icon}
              label={t(option.labelKey)}
              isSelected={state.menstrualFlowIntensity === option.id}
              onPress={() => handleFlowSelect(option.id)}
              labelSize="sm"
            />
          ))}
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  spacer: {
    height: 16,
  },
  flowRow: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
});
